import React, { useEffect, useState, useRef } from 'react';
import {
    getAdminNotices, addAdminNotice, updateAdminNotice, deleteAdminNotice,
    getAdminNotifications, addAdminNotification, updateAdminNotification, deleteAdminNotification,
    getAdminUsers
} from '../api';
import { Plus, Edit2, Trash2, X, Check, Bell, Info, ChevronDown } from 'lucide-react';

const Notice = () => {
    const [activeTab, setActiveTab] = useState('notice'); // 'notice' or 'notification'
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState(null);

    // UI states
    const [users, setUsers] = useState([]);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [notifLangTab, setNotifLangTab] = useState('default');

    // Form state
    const [formData, setFormData] = useState({
        lang: 'ko',
        title: '',
        link: '', // for notice
        titles: { default: '', ko: '', en: '', ja: '' }, // for notification
        contents: { default: '', ko: '', en: '', ja: '' }, // for notification
        target_type: 'all', // for notification
        target_user_ids: [], // for notification
        is_active: true,
        start_date: '', // for notification
        end_date: '' // for notification
    });

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsUserDropdownOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchUsers = async () => {
        try {
            const res = await getAdminUsers(1, 1000);
            console.log(res);
            if (res.data.success) {
                setUsers(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
    };

    useEffect(() => {
        fetchData();
        if (activeTab === 'notification' && users.length === 0) {
            fetchUsers();
        }
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = activeTab === 'notice' ? await getAdminNotices() : await getAdminNotifications();
            if (res.data.success) {
                setData(res.data.data);
            }
        } catch (error) {
            console.error(`Failed to fetch ${activeTab}s`, error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = (item = null) => {
        if (item) {
            setEditingItem(item);
            if (activeTab === 'notice') {
                setFormData({
                    lang: item.lang,
                    title: item.title,
                    link: item.link || '',
                    is_active: item.is_active
                });
            } else {
                setFormData({
                    titles: item.titles || { default: '', ko: '', en: '', ja: '' },
                    contents: item.contents || { default: '', ko: '', en: '', ja: '' },
                    target_type: item.target_type || 'all',
                    target_user_ids: item.target_user_id ? item.target_user_id.split(',') : [],
                    is_active: item.is_active,
                    start_date: item.start_date ? new Date(item.start_date).toISOString().split('T')[0] : '',
                    end_date: item.end_date ? new Date(item.end_date).toISOString().split('T')[0] : ''
                });
                setNotifLangTab('default');
            }
        } else {
            setEditingItem(null);
            setFormData({
                lang: 'ko',
                title: '',
                link: '',
                titles: { default: '', ko: '', en: '', ja: '' },
                contents: { default: '', ko: '', en: '', ja: '' },
                target_type: 'all',
                target_user_ids: [],
                is_active: true,
                start_date: '',
                end_date: ''
            });
            setNotifLangTab('default');
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const submissionData = new FormData();
            submissionData.append('lang', formData.lang);
            submissionData.append('title', formData.title);
            submissionData.append('is_active', formData.is_active);

            if (activeTab === 'notice') {
                submissionData.append('link', formData.link);
                if (editingItem) {
                    await updateAdminNotice(editingItem.id, submissionData);
                } else {
                    await addAdminNotice(submissionData);
                }
            } else {
                submissionData.append('titles', JSON.stringify(formData.titles || {}));
                submissionData.append('contents', JSON.stringify(formData.contents || {}));
                submissionData.append('target_type', formData.target_type);
                if (formData.target_user_ids && formData.target_user_ids.length > 0) {
                    submissionData.append('target_user_id', formData.target_user_ids.join(','));
                } else {
                    submissionData.append('target_user_id', '');
                }
                submissionData.append('start_date', formData.start_date || '');
                submissionData.append('end_date', formData.end_date || '');
                if (editingItem) {
                    await updateAdminNotification(editingItem.id, submissionData);
                } else {
                    await addAdminNotification(submissionData);
                }
            }
            fetchData();
            handleCloseModal();
        } catch (error) {
            console.error(`Failed to save ${activeTab}`, error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm(`Are you sure you want to delete this ${activeTab}?`)) {
            try {
                if (activeTab === 'notice') {
                    await deleteAdminNotice(id);
                } else {
                    await deleteAdminNotification(id);
                }
                fetchData();
            } catch (error) {
                console.error(`Failed to delete ${activeTab}`, error);
            }
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div className="flex bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => setActiveTab('notice')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'notice' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Notice
                    </button>
                    <button
                        onClick={() => setActiveTab('notification')}
                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'notification' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Notification
                    </button>
                </div>
                <button
                    onClick={() => handleOpenModal()}
                    className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                    <Plus size={20} /> Add {activeTab === 'notice' ? 'Notice' : 'Notification'}
                </button>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-100">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lang</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                                {activeTab === 'notice' ? (
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Link</th>
                                ) : (
                                    <>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Target</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                                    </>
                                )}
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">Loading...</td>
                                </tr>
                            ) : (!data || data.length === 0) ? (
                                <tr>
                                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No items found.</td>
                                </tr>
                            ) : (
                                data.map((item) => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 uppercase">
                                            {activeTab === 'notice' ? (item.lang === 'default' ? 'DEF' : item.lang) : 'MULTI'}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                                            {activeTab === 'notice' ? item.title : (item.titles ? (item.titles.default || item.titles.ko || item.titles.en || 'No title') : item.title)}
                                        </td>
                                        {activeTab === 'notice' ? (
                                            <td className="px-6 py-4 text-sm text-blue-600 max-w-xs truncate">
                                                <a href={item.link} target="_blank" rel="noopener noreferrer">{item.link}</a>
                                            </td>
                                        ) : (
                                            <>
                                                <td className="px-6 py-4 text-sm text-gray-600 capitalize">{item.target_type}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{item.target_user_id || '-'}</td>
                                            </>
                                        )}
                                        <td className="px-6 py-4 whitespace-nowrap text-center">
                                            {item.is_active ? (
                                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-semibold">Active</span>
                                            ) : (
                                                <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-semibold">Inactive</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-500">
                                            {activeTab === 'notification' && (item.start_date || item.end_date) ? (
                                                <div className="flex flex-col text-xs">
                                                    <span>{item.start_date ? new Date(item.start_date).toLocaleDateString() : '-'}</span>
                                                    <span className="text-gray-300">~</span>
                                                    <span>{item.end_date ? new Date(item.end_date).toLocaleDateString() : '-'}</span>
                                                </div>
                                            ) : (
                                                new Date(item.created_at).toLocaleDateString()
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <div className="flex justify-end gap-2">
                                                <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:text-blue-900 p-1">
                                                    <Edit2 size={18} />
                                                </button>
                                                <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900 p-1">
                                                    <Trash2 size={18} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-md my-8 animate-in fade-in zoom-in duration-200 overflow-visible">
                        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingItem ? 'Edit' : 'Add'} {activeTab === 'notice' ? 'Notice' : 'Notification'}
                            </h2>
                            <button onClick={handleCloseModal} className="text-gray-500 hover:text-gray-700">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-visible">
                            <div className="grid grid-cols-2 gap-4">
                                {activeTab === 'notice' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
                                        <select
                                            value={formData.lang}
                                            onChange={(e) => setFormData({ ...formData, lang: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                            required
                                        >
                                            <option value="ko">Korean (ko)</option>
                                            <option value="en">English (en)</option>
                                            <option value="ja">Japanese (ja)</option>
                                        </select>
                                    </div>
                                )}
                                {activeTab === 'notification' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Target</label>
                                        <select
                                            value={formData.target_type}
                                            onChange={(e) => setFormData({ ...formData, target_type: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                            required
                                        >
                                            <option value="all">All Users</option>
                                            <option value="user">Specific User(s)</option>
                                        </select>
                                    </div>
                                )}
                            </div>

                            {activeTab === 'notification' && formData.target_type === 'user' && (
                                <div className="relative z-30" ref={dropdownRef}>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Target Users</label>
                                    <div
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 cursor-pointer flex justify-between items-center bg-white"
                                        onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                    >
                                        <span className="truncate text-gray-600 text-sm">
                                            {formData.target_user_ids.length > 0
                                                ? `${formData.target_user_ids.length} user(s) selected`
                                                : 'Select users...'}
                                        </span>
                                        <ChevronDown size={16} className="text-gray-400" />
                                    </div>
                                    {isUserDropdownOpen && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-xl max-h-60 overflow-y-auto py-1">
                                            {users.map(u => (
                                                <div
                                                    key={u.id}
                                                    className="px-3 py-2 hover:bg-gray-50 flex items-center gap-2 cursor-pointer"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        const idStr = u.id.toString();
                                                        const newIds = formData.target_user_ids.includes(idStr)
                                                            ? formData.target_user_ids.filter(id => id !== idStr)
                                                            : [...formData.target_user_ids, idStr];
                                                        setFormData({ ...formData, target_user_ids: newIds });
                                                    }}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.target_user_ids.includes(u.id.toString())}
                                                        readOnly
                                                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500 cursor-pointer"
                                                    />
                                                    <span className="text-sm text-gray-700">{u.email} {u.username ? `(${u.username})` : ''}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'notice' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                                    <input
                                        type="text"
                                        value={formData.title}
                                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Enter title"
                                        required
                                    />
                                </div>
                            )}

                            {activeTab === 'notice' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Link URL</label>
                                    <input
                                        type="url"
                                        value={formData.link}
                                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="https://example.com/..."
                                    />
                                </div>
                            )}

                            {activeTab === 'notification' && (
                                <div className="border border-blue-100 rounded-lg p-4 bg-blue-50/30 space-y-3">
                                    <label className="block text-sm font-medium text-gray-700">Notification Content (Multi-Language)</label>
                                    <div className="flex bg-white border border-gray-200 rounded-t-lg overflow-hidden">
                                        {['default', 'ko', 'en', 'ja'].map(lang => (
                                            <button
                                                key={lang}
                                                type="button"
                                                onClick={() => setNotifLangTab(lang)}
                                                className={`flex-1 py-1.5 text-xs font-semibold text-center transition-colors ${notifLangTab === lang ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50 uppercase'}`}
                                            >
                                                {lang === 'default' ? 'DEF' : lang.toUpperCase()}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="bg-white border-x border-b border-gray-200 rounded-b-lg p-4 space-y-3">
                                        <div>
                                            <input
                                                type="text"
                                                value={formData.titles?.[notifLangTab] || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, titles: { ...prev.titles, [notifLangTab]: e.target.value } }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder={`Title (${notifLangTab.toUpperCase()})`}
                                            />
                                        </div>
                                        <div>
                                            <textarea
                                                value={formData.contents?.[notifLangTab] || ''}
                                                onChange={(e) => setFormData(prev => ({ ...prev, contents: { ...prev.contents, [notifLangTab]: e.target.value } }))}
                                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                                placeholder={`Content (${notifLangTab.toUpperCase()})`}
                                                rows={3}
                                            />
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1"><Info size={14} /> 'DEF' is used if the user's preferred language is not provided.</p>
                                </div>
                            )}

                            {activeTab === 'notification' && (
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                        <input
                                            type="date"
                                            value={formData.start_date}
                                            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                        <input
                                            type="date"
                                            value={formData.end_date}
                                            onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="flex items-center gap-2 py-2">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-gray-700 font-semibold">Active Status</label>
                            </div>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={handleCloseModal} className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                                    Cancel
                                </button>
                                <button type="submit" className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2">
                                    <Check size={18} /> {editingItem ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notice;
